import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Edit, Trash2, Upload, ChevronLeft, Play, Pause, Volume2, X, Maximize2 } from 'lucide-react';

import { videoAPI, categoryAPI } from '../../services/api';
import CategoryCreateDialog from './CategoryCreateDialog';

const VideoSidebar = ({ isOpen, onClose, onVideoSelect, template_content, settemplate_content, currentPageIndex = 0 }) => {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [fullscreenVideo, setFullscreenVideo] = useState(null); // video object shown fullscreen
  const [addDialogVideo, setAddDialogVideo] = useState(null); // video pending "Add to Timeline"
  const [addStartTime, setAddStartTime] = useState('0');
  const [addLayer, setAddLayer] = useState('1');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryCreateDialog, setShowCategoryCreateDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    title: '',
    creator: '',
    source: '',
    tags: '',
    isLoop: false,
    file: null,
    videoUrl: '',
    detectedAttributes: null,
    // Video processing options
    enableResize: false,
    targetWidth: '',
    targetHeight: '',
    extractAudio: true,
    addToTemplate: true
  });
  const videoRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      fetchVideos();
      fetchCategories();
    }
  }, [isOpen, selectedCategory, searchTerm]);

  // Fetch subcategories when upload form category changes
  useEffect(() => {
    if (uploadForm.category && uploadForm.category !== '') {
      fetchSubCategories(uploadForm.category);
    } else {
      setSubCategories([]);
    }
  }, [uploadForm.category]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const params = {
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        search: searchTerm || undefined,
        limit: 50
      };
      
      const response = await videoAPI.getAll(params);
      if (response.data.success) {
        setVideos(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryAPI.getAll({ type: 'video' });
      if (response.data.success) {
        const categoriesData = response.data.data || [];
        // Sort categories by name
        categoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubCategories = async (parentId) => {
    try {
      setLoadingSubCategories(true);
      const response = await categoryAPI.getAll({ parentId, type: 'video' });
      if (response.data.success) {
        const subCategoriesData = response.data.data || [];
        // Sort subcategories by name
        subCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setSubCategories(subCategoriesData);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const handlePlay = (videoId) => {
    // Stop currently playing video
    if (currentlyPlaying && videoRefs.current[currentlyPlaying]) {
      try {
        videoRefs.current[currentlyPlaying].pause();
        videoRefs.current[currentlyPlaying].currentTime = 0;
      } catch (e) { /* ignore */ }
    }

    // Toggle: the streamed <video> is mounted conditionally and autoPlays, so we
    // only need to flip which card is active.
    setCurrentlyPlaying(currentlyPlaying === videoId ? null : videoId);
  };

  const handleVideoEnd = (videoId) => {
    if (currentlyPlaying === videoId) {
      setCurrentlyPlaying(null);
    }
  };

  // Function to add items to current template
  const addItemsToTemplate = async (video, audio) => {
    try {
      const currentPage = template_content.pages[currentPageIndex];
      if (!currentPage) {
        alert('No current page found');
        return;
      }

      const newItems = [];
      let nextLayer = Math.max(...currentPage.items.map(item => item.layer || 0), 0) + 1;

      // Add video item
      if (video) {
        const videoItem = {
          x: 0,
          y: 0,
          id: `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          src: video.storage_url,
          loop: false,
          time: {
            start: 0,
            duration: video.duration || 30
          },
          type: 'video',
          layer: nextLayer++,
          muted: false,
          width: video.width || 720,
          height: video.height || 1280,
          locked: false,
          volume: 1,
          opacity: 1,
          autoplay: false,
          rotation: 0
        };
        newItems.push(videoItem);
      }

      // Add audio item
      if (audio) {
        const audioItem = {
          id: `audio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file: {
            url: audio.storage_url,
            name: audio.name,
            duration: audio.duration || 30
          },
          name: audio.name,
          time: {
            start: 0,
            duration: audio.duration || 30
          },
          trim: {
            end: audio.duration || 30,
            start: 0
          },
          type: 'audio',
          layer: -1,
          start: 0,
          trackId: 0,
          duration: audio.duration || 30,
          isVisual: false,
          settings: {
            pan: 0,
            fadeIn: 0,
            volume: 0.9,
            fadeOut: 0
          }
        };
        newItems.push(audioItem);
      }

      // Update template content
      const updatedTemplate = {
        ...template_content,
        pages: template_content.pages.map((page, index) =>
          index === currentPageIndex
            ? { ...page, items: [...page.items, ...newItems] }
            : page
        )
      };

      settemplate_content(updatedTemplate);
      alert(`✅ Added ${newItems.length} item(s) to template!`);
    } catch (error) {
      console.error('Error adding items to template:', error);
      alert('Failed to add items to template');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (uploadMode === 'file') {
      if (!uploadForm.file) {
        alert('Please select a video file');
        return;
      }
    } else if (uploadMode === 'url') {
      if (!uploadForm.videoUrl || !uploadForm.videoUrl.trim()) {
        alert('Please enter a video URL');
        return;
      }
      // Validate URL format
      try {
        const url = new URL(uploadForm.videoUrl);
        if (!url.protocol.startsWith('http')) {
          alert('Invalid URL. Must start with http:// or https://');
          return;
        }
      } catch (error) {
        alert('Invalid URL format');
        return;
      }
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadStage('Preparing upload...');

      const formData = new FormData();
      
      if (uploadMode === 'file') {
        formData.append('videoFile', uploadForm.file);
      } else if (uploadMode === 'url') {
        formData.append('videoUrl', uploadForm.videoUrl.trim());
      }
      
      formData.append('name', uploadForm.name);
      formData.append('description', uploadForm.description);
      if (uploadForm.category) {
        formData.append('category', uploadForm.category);
      }
      if (uploadForm.subcategory) {
        formData.append('subcategory', uploadForm.subcategory);
      }
      formData.append('title', uploadForm.title);
      formData.append('creator', uploadForm.creator);
      formData.append('source', uploadForm.source);
      formData.append('tags', uploadForm.tags);
      formData.append('isLoop', uploadForm.isLoop);

      // Add processing options
      formData.append('enableResize', uploadForm.enableResize);
      if (uploadForm.enableResize) {
        formData.append('targetWidth', uploadForm.targetWidth);
        formData.append('targetHeight', uploadForm.targetHeight);
      }
      formData.append('extractAudio', uploadForm.extractAudio);
      formData.append('addToTemplate', uploadForm.addToTemplate);

      setUploadStage(uploadMode === 'file' ? 'Uploading video...' : 'Downloading video from URL...');
      setUploadProgress(10);

      // Determine API endpoint
      const apiEndpoint = uploadMode === 'file' ? '/api/v1/videos' : '/api/v1/videos/import-from-url';

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      // Track upload progress (only for file uploads)
      if (uploadMode === 'file') {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 70; // 70% for upload
            setUploadProgress(Math.round(percentComplete));
            setUploadStage(`Uploading... ${Math.round(percentComplete)}%`);
          }
        });
      } else {
        // For URL import, simulate progress
        setUploadProgress(30);
        setUploadStage('Downloading video...');
      }

      // Handle response
      const response = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(75);
            setUploadStage('Processing video...');
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              json: () => Promise.resolve(JSON.parse(xhr.responseText))
            });
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        xhr.open('POST', apiEndpoint);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
        xhr.send(formData);
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const { video, audio, processing, addToTemplate } = result.data;

          // Show success message with processing details
          let message = `✅ Video uploaded successfully!`;
          if (processing.resized) {
            message += `\n📐 Resized from ${processing.original_dimensions?.width}×${processing.original_dimensions?.height} to ${processing.new_dimensions?.width}×${processing.new_dimensions?.height}`;
          }
          if (processing.audio_extracted) {
            message += `\n🎵 Audio track extracted`;
          }

          // Show confirmation dialog if user wants to add to template
          if (addToTemplate && (video || audio)) {
            const confirmMessage = `${message}\n\n🎬 Do you want to add the processed items to your current template?`;
            if (window.confirm(confirmMessage)) {
              await addItemsToTemplate(video, audio);
            }
          } else {
            alert(message);
          }

          setShowUploadModal(false);
          setUploadMode('file');
          setUploadForm({
            name: '',
            description: '',
            category: '',
            subcategory: '',
            title: '',
            creator: '',
            source: '',
            tags: '',
            isLoop: false,
            file: null,
            videoUrl: '',
            detectedAttributes: null,
            enableResize: false,
            targetWidth: '',
            targetHeight: '',
            extractAudio: true,
            addToTemplate: true
          });
          fetchVideos();
        }
      } else {
        const errorData = await response.json();
        alert(`Upload failed: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  // Resolve the real/direct asset URL for a video (CDN/MinIO), falling back to the stream endpoint.
  const resolveVideoSrc = (video) => (
    video.storage_url ||
    video.storageUrl ||
    video.directUrl ||
    video.filePath ||
    (video.id ? `/api/v1/videos/${video.id}/stream` : '')
  );

  // Open the "Add to Timeline" dialog, defaulting the layer to the next free layer on the page.
  const openAddDialog = (video) => {
    const currentPage = template_content?.pages?.[currentPageIndex] || {};
    const currentItems = currentPage.items || [];
    const nextLayer = Math.max(0, ...currentItems.map(item => item.layer || 0)) + 1;
    setAddDialogVideo(video);
    setAddStartTime('0');
    setAddLayer(String(nextLayer));
  };

  // Confirm the dialog: build the timeline item in the canonical format and append it.
  const handleConfirmAddVideo = () => {
    const video = addDialogVideo;
    if (!video) return;

    // Allow an external handler to intercept selection first.
    if (typeof onVideoSelect === 'function') {
      try {
        const handled = onVideoSelect(video);
        if (handled) {
          setAddDialogVideo(null);
          onClose();
          return;
        }
      } catch (error) {
        console.error('Error forwarding video selection:', error);
      }
    }

    const startTime = Math.max(0, parseFloat(addStartTime) || 0);
    const layer = parseInt(addLayer, 10);
    const videoDuration = video.duration || 10;
    const src = resolveVideoSrc(video);

    const newVideoItem = {
      x: 0,
      y: 0,
      id: `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      src,
      loop: false,
      time: {
        start: startTime,
        duration: videoDuration
      },
      type: 'video',
      layer: Number.isFinite(layer) ? layer : 1,
      muted: false,
      width: video.width || 720,
      height: video.height || 1280,
      locked: false,
      volume: 1,
      opacity: 1,
      autoplay: false,
      rotation: 0
    };

    const currentPage = template_content?.pages?.[currentPageIndex] || {};
    const currentItems = currentPage.items || [];
    const updatedTemplate = {
      ...template_content,
      pages: template_content.pages.map((page, idx) =>
        idx === currentPageIndex
          ? { ...page, items: [...currentItems, newVideoItem] }
          : page
      )
    };

    settemplate_content(updatedTemplate);
    setAddDialogVideo(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">Video Library</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload Video
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Video Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {videos.map((video) => (
                <div key={video.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                  {/* Video Preview */}
                  <div className="relative aspect-video bg-gray-900">
                    {currentlyPlaying === video.id ? (
                      // Only stream the actual video while this card is playing.
                      <video
                        ref={(el) => {
                          if (el) videoRefs.current[video.id] = el;
                        }}
                        onEnded={() => handleVideoEnd(video.id)}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        poster={video.thumbnail_url || video.thumbnailUrl || undefined}
                        autoPlay
                        controls
                        muted
                      >
                        <source src={`/api/v1/videos/${video.id}/stream`} type={video.mimeType || 'video/mp4'} />
                      </video>
                    ) : (video.thumbnail_url || video.thumbnailUrl) ? (
                      // Saved thumbnail (middle frame generated at upload) as the poster.
                      <img
                        src={video.thumbnail_url || video.thumbnailUrl}
                        alt={video.name || video.filename || 'Video thumbnail'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      // No thumbnail available — simple placeholder.
                      <div className="w-full h-full flex items-center justify-center text-gray-600 text-3xl">
                        🎬
                      </div>
                    )}

                    {/* Play + Fullscreen overlay */}
                    <div className="absolute inset-0 flex items-center justify-center gap-2">
                      <button
                        onClick={() => handlePlay(video.id)}
                        className="bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all"
                        title={currentlyPlaying === video.id ? 'Pause' : 'Play'}
                      >
                        {currentlyPlaying === video.id ? (
                          <Pause className="w-6 h-6" />
                        ) : (
                          <Play className="w-6 h-6" />
                        )}
                      </button>
                      <button
                        onClick={() => setFullscreenVideo(video)}
                        className="bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all"
                        title="View fullscreen"
                      >
                        <Maximize2 className="w-6 h-6" />
                      </button>
                    </div>
                  </div>

                  {/* Video Info */}
                  <div className="p-3">
                    <h3 className="font-medium text-gray-900 truncate" title={video.name || video.filename}>
                      {video.name || video.filename || 'Untitled video'}
                    </h3>
                    {video.category_name && (
                      <p className="text-xs text-gray-600 mt-0.5 truncate" title={video.category_name}>
                        📁 {video.category_name}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      {video.duration ? `${Math.round(video.duration)}s` : 'Unknown duration'}
                      {video.width && video.height && ` • ${video.width}×${video.height}`}
                    </p>
                    {video.created_at && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Uploaded {new Date(video.created_at).toLocaleString()}
                      </p>
                    )}

                    {/* Add to Timeline Button */}
                    <button
                      onClick={() => openAddDialog(video)}
                      className="w-full mt-2 bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      Add to Timeline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <div className="text-4xl mb-4">🎬</div>
              <p className="text-center">No videos found</p>
              <p className="text-sm text-center mt-1">Upload videos to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Add Video</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadMode('file');
                  setUploadForm({
                    name: '',
                    description: '',
                    category: '',
                    subcategory: '',
                    title: '',
                    creator: '',
                    source: '',
                    tags: '',
                    isLoop: false,
                    file: null,
                    videoUrl: '',
                    detectedAttributes: null,
                    enableResize: false,
                    targetWidth: '',
                    targetHeight: '',
                    extractAudio: true,
                    addToTemplate: true
                  });
                  setUploadMode('file');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Upload Mode Tabs */}
            <div className="flex border-b">
              <button
                type="button"
                onClick={() => setUploadMode('file')}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                  uploadMode === 'file'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Upload File
              </button>
              <button
                type="button"
                onClick={() => setUploadMode('url')}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                  uploadMode === 'url'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Import from URL
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-4 space-y-4">
              {uploadMode === 'file' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video File *
                  </label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                    const file = e.target.files[0]
                    if (!file) return

                    // Check file size (200MB limit)
                    const maxSize = 200 * 1024 * 1024 // 200MB in bytes
                    if (file.size > maxSize) {
                      alert(`File size too large. Maximum allowed size is 200MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`)
                      e.target.value = '' // Clear the input
                      return
                    }

                    const name = file.name.replace(/\.[^/.]+$/, '')

                    // Extract video attributes using HTML5 video element
                    const video = document.createElement('video')
                    video.preload = 'metadata'

                    video.onloadedmetadata = () => {
                      console.log('📹 Video metadata loaded:', {
                        duration: video.duration,
                        width: video.videoWidth,
                        height: video.videoHeight
                      })

                      setUploadForm(prev => ({
                        ...prev,
                        file,
                        name: prev.name || name,
                        // Store detected attributes for display
                        detectedAttributes: {
                          duration: video.duration,
                          width: video.videoWidth,
                          height: video.videoHeight,
                          size: file.size,
                          type: file.type
                        }
                      }))

                      // Clean up
                      URL.revokeObjectURL(video.src)
                    }

                    video.onerror = () => {
                      console.warn('⚠️ Could not load video metadata')
                      setUploadForm(prev => ({
                        ...prev,
                        file,
                        name: prev.name || name,
                        detectedAttributes: {
                          size: file.size,
                          type: file.type
                        }
                      }))
                      URL.revokeObjectURL(video.src)
                    }

                    video.src = URL.createObjectURL(file)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />

                {/* Display detected video attributes */}
                {uploadForm.detectedAttributes && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-medium text-blue-900 mb-2">📹 Video Information</p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                      <div>Size: {(uploadForm.detectedAttributes.size / (1024 * 1024)).toFixed(1)} MB</div>
                      <div>Type: {uploadForm.detectedAttributes.type}</div>
                      {uploadForm.detectedAttributes.duration && (
                        <div>Duration: {uploadForm.detectedAttributes.duration.toFixed(1)}s</div>
                      )}
                      {uploadForm.detectedAttributes.width && uploadForm.detectedAttributes.height && (
                        <div>Resolution: {uploadForm.detectedAttributes.width} × {uploadForm.detectedAttributes.height}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Video Processing Options */}
                {uploadForm.detectedAttributes && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">🔧 Processing Options</h4>

                    {/* Resize Option */}
                    <div className="space-y-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={uploadForm.enableResize}
                          onChange={(e) => setUploadForm(prev => ({
                            ...prev,
                            enableResize: e.target.checked,
                            targetWidth: e.target.checked ? prev.detectedAttributes?.width || '' : '',
                            targetHeight: e.target.checked ? prev.detectedAttributes?.height || '' : ''
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Resize Video</span>
                      </label>

                      {uploadForm.enableResize && (
                        <div className="ml-6 grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Width (px)</label>
                            <input
                              type="number"
                              value={uploadForm.targetWidth}
                              onChange={(e) => setUploadForm(prev => ({ ...prev, targetWidth: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="1920"
                              min="1"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Height (px)</label>
                            <input
                              type="number"
                              value={uploadForm.targetHeight}
                              onChange={(e) => setUploadForm(prev => ({ ...prev, targetHeight: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="1080"
                              min="1"
                            />
                          </div>
                        </div>
                      )}

                      {/* Audio Extraction Option */}
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={uploadForm.extractAudio}
                          onChange={(e) => setUploadForm(prev => ({ ...prev, extractAudio: e.target.checked }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Extract Audio Track</span>
                      </label>

                      {/* Add to Template Option */}
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={uploadForm.addToTemplate}
                          onChange={(e) => setUploadForm(prev => ({ ...prev, addToTemplate: e.target.checked }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Add to Current Template</span>
                      </label>
                    </div>
                  </div>
                )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video URL *
                  </label>
                  <input
                    type="url"
                    value={uploadForm.videoUrl}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, videoUrl: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/video.mp4"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a direct link to a video file (MP4, MOV, AVI, etc.)
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Video name"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Category *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCategoryCreateDialog(true)}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                      disabled={uploading}
                    >
                      <Plus className="w-3 h-3" />
                      <span>Add missing category</span>
                    </button>
                  </div>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subcategory
                  </label>
                  <select
                    value={uploadForm.subcategory}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, subcategory: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!uploadForm.category || loadingSubCategories}
                  >
                    <option value="">Select Subcategory</option>
                    {subCategories.map(subcategory => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Video description"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isLoop"
                  checked={uploadForm.isLoop}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, isLoop: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isLoop" className="text-sm text-gray-700">
                  Loop video
                </label>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{uploadStage}</span>
                    <span className="text-sm text-gray-500">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {uploadProgress < 70 ? 'Uploading video file...' :
                     uploadProgress < 90 ? 'Processing video...' :
                     'Finalizing upload...'}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || (uploadMode === 'file' && !uploadForm.file) || (uploadMode === 'url' && !uploadForm.videoUrl?.trim())}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading 
                    ? (uploadStage || 'Processing...') 
                    : (uploadMode === 'file' ? 'Upload Video' : 'Import Video')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fullscreen Video Preview */}
      {fullscreenVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[70]"
          onClick={() => setFullscreenVideo(null)}
        >
          <button
            onClick={() => setFullscreenVideo(null)}
            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 p-2 rounded-full hover:bg-opacity-70 transition-all"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl w-full px-6" onClick={(e) => e.stopPropagation()}>
            <video
              src={resolveVideoSrc(fullscreenVideo)}
              poster={fullscreenVideo.thumbnail_url || fullscreenVideo.thumbnailUrl || undefined}
              className="w-full max-h-[80vh] object-contain bg-black rounded"
              controls
              autoPlay
            />
            <div className="mt-3 text-white">
              <h3 className="text-lg font-medium">
                {fullscreenVideo.name || fullscreenVideo.filename || 'Untitled video'}
              </h3>
              <p className="text-sm text-gray-300 mt-1">
                {fullscreenVideo.category_name ? `📁 ${fullscreenVideo.category_name} • ` : ''}
                {fullscreenVideo.duration ? `${Math.round(fullscreenVideo.duration)}s` : ''}
                {fullscreenVideo.width && fullscreenVideo.height ? ` • ${fullscreenVideo.width}×${fullscreenVideo.height}` : ''}
                {fullscreenVideo.created_at ? ` • Uploaded ${new Date(fullscreenVideo.created_at).toLocaleString()}` : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add to Timeline Dialog */}
      {addDialogVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Add to Timeline</h3>
              <button
                onClick={() => setAddDialogVideo(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{addDialogVideo.name || addDialogVideo.filename || 'Untitled video'}</span>
                {addDialogVideo.duration ? (
                  <span className="text-gray-500"> • {Math.round(addDialogVideo.duration)}s</span>
                ) : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time (seconds)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={addStartTime}
                  onChange={(e) => setAddStartTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Layer Number
                </label>
                <input
                  type="number"
                  step="1"
                  value={addLayer}
                  onChange={(e) => setAddLayer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1"
                />
                <p className="mt-1 text-xs text-gray-500">Higher layers render on top.</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setAddDialogVideo(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAddVideo}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Create Dialog */}
      {showCategoryCreateDialog && (
        <CategoryCreateDialog
          isOpen={showCategoryCreateDialog}
          onClose={() => setShowCategoryCreateDialog(false)}
          onCategoryCreated={(newCategory) => {
            // Refresh categories list
            fetchCategories();
            // Auto-select the newly created category
            setUploadForm(prev => ({ ...prev, category: newCategory.id }));
            setShowCategoryCreateDialog(false);
          }}
          type="video"
        />
      )}
    </div>
  );
};

export default VideoSidebar;
